import * as THREE from 'three';
import { DecalGeometry } from 'three/examples/jsm/geometries/DecalGeometry.js';
import type { StickerDefinition, StickerInstance, NameTag } from './sticker-types';
import { getStickerSlots, MAX_NAME_TAG_LENGTH, MAX_STICKERS_PER_WEAPON } from './sticker-types';

// ---------------------------------------------------------------------------
// Sticker Renderer
// ---------------------------------------------------------------------------
// Renders stickers as projected decals on weapon meshes using DecalGeometry.
// Also handles name tag text rendering on weapons via canvas textures.
// ---------------------------------------------------------------------------

/**
 * Renders stickers as projected decals on weapon meshes.
 * Also handles name tag text rendering on weapons.
 */
/** Shared fallback placeholder texture — reused across all load errors to avoid leaks */
let sharedPlaceholderTexture: THREE.Texture | null = null;
function getPlaceholderTexture(): THREE.Texture {
  if (!sharedPlaceholderTexture) {
    sharedPlaceholderTexture = new THREE.Texture();
  }
  return sharedPlaceholderTexture;
}

function createFallbackStickerTexture(def: StickerDefinition): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return getPlaceholderTexture();
  }

  const background = def.holographic
    ? ctx.createLinearGradient(0, 0, canvas.width, canvas.height)
    : null;
  if (background) {
    background.addColorStop(0, '#8a6dff');
    background.addColorStop(0.5, '#33d1ff');
    background.addColorStop(1, '#ffe066');
    ctx.fillStyle = background;
  } else {
    ctx.fillStyle = def.foil ? '#d8c37a' : '#2a2f3a';
  }
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
  ctx.lineWidth = 8;
  ctx.strokeRect(16, 16, canvas.width - 32, canvas.height - 32);

  ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
  ctx.fillRect(24, 168, canvas.width - 48, 56);

  const initials = def.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'ST';

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = 'bold 96px "Courier New", monospace';
  ctx.fillText(initials, canvas.width / 2, 96);

  ctx.font = 'bold 26px "Courier New", monospace';
  ctx.fillText(def.collection.slice(0, 14), canvas.width / 2, 196);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  return texture;
}

export class StickerRenderer {
  private textureCache = new Map<number, THREE.Texture>();
  private stickerMeshes = new WeakMap<THREE.Group, THREE.Mesh[]>();
  private nameTagMeshes = new WeakMap<THREE.Group, THREE.Mesh>();
  private textureLoader = new THREE.TextureLoader();
  /**
   * Per-shader uniforms for holographic stickers. onBeforeCompile produces a
   * fresh uniforms object per material, so we capture each here and tick them
   * all from `update(time)` so the iridescence animates.
   */
  private holographicShaders: { uTime: { value: number } }[] = [];
  /** Maps each holographic material to its uniforms entry so removeStickers can splice on dispose. */
  private holographicByMaterial = new WeakMap<THREE.Material, { uTime: { value: number } }>();

  /**
   * Advance animated uniforms (currently holographic stickers' uTime).
   * Call once per frame with elapsed seconds since app start. uTime is
   * wrapped to [0, 2π) to keep float32 precision indefinite.
   */
  update(elapsedSeconds: number): void {
    const wrapped = elapsedSeconds % (Math.PI * 2);
    for (const uniforms of this.holographicShaders) {
      uniforms.uTime.value = wrapped;
    }
  }

  /**
   * Apply stickers to a weapon mesh group.
   * Creates decal geometries at predefined slot positions.
   */
  async applyStickers(
    weaponGroup: THREE.Group,
    weaponId: string,
    stickers: (StickerInstance | null)[],
    stickerDefs: ReadonlyMap<number, StickerDefinition>,
  ): Promise<void> {
    // Remove existing stickers first
    this.removeStickers(weaponGroup);

    const slots = getStickerSlots(weaponId);
    const meshes: THREE.Mesh[] = [];

    // Find the main weapon mesh for decal projection (largest geometry)
    let targetMesh: THREE.Mesh | null = null;
    weaponGroup.traverse((child) => {
      if (child instanceof THREE.Mesh && !child.userData.statTrak && !child.userData.sticker) {
        const childCount = child.geometry.attributes.position?.count ?? 0;
        const bestCount = targetMesh?.geometry.attributes.position?.count ?? 0;
        if (!targetMesh || childCount > bestCount) {
          targetMesh = child as THREE.Mesh;
        }
      }
    });
    if (!targetMesh) return;

    // F-CM-003: iterate up to MAX_STICKERS_PER_WEAPON, not slots.length, so 5
    // stickers on a 4-slot weapon (AWP/glock/deagle/_default) aren't silently
    // dropped. Resolve each sticker by its recorded slot index, clamped to the
    // weapon's actual slot count — never silently re-route to a different
    // visual position than the player applied.
    for (let i = 0; i < stickers.length && i < MAX_STICKERS_PER_WEAPON; i++) {
      const sticker = stickers[i];
      if (!sticker) continue;

      const def = stickerDefs.get(sticker.stickerId);
      if (!def) continue;

      // Skip when the recorded slot exceeds this weapon's slot count: the
      // sticker was authored for a layout this weapon doesn't expose, so
      // displaying it at a different slot would mislead the player.
      if (sticker.slot < 0 || sticker.slot >= slots.length) continue;
      const slot = slots[sticker.slot];
      if (!slot) continue;

      // Load sticker texture
      const texture = await this.loadStickerTexture(def);

      // Create decal material
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        depthWrite: false,
        depthTest: true,
        polygonOffset: true,
        polygonOffsetFactor: -4,
        opacity: 1.0 - sticker.wear * 0.8, // Wear reduces opacity
      });

      // Apply holographic shimmer via onBeforeCompile
      if (def.holographic) {
        this.applyHolographicEffect(material);
      }

      // Calculate decal position with user offsets
      const position = new THREE.Vector3(
        slot.position[0] + sticker.offsetX * 0.01,
        slot.position[1] + sticker.offsetY * 0.01,
        slot.position[2],
      );

      const rotation = new THREE.Euler(
        slot.rotation[0],
        slot.rotation[1],
        slot.rotation[2] + (sticker.rotation * Math.PI / 180),
      );

      const size = new THREE.Vector3(slot.scale, slot.scale, slot.scale * 2);

      // Create decal geometry projected onto weapon mesh
      const decalGeom = new DecalGeometry(targetMesh, position, rotation, size);
      const decalMesh = new THREE.Mesh(decalGeom, material);
      decalMesh.userData.sticker = true;
      decalMesh.userData.stickerSlot = i;
      decalMesh.renderOrder = 100 + i;

      weaponGroup.add(decalMesh);
      meshes.push(decalMesh);
    }

    this.stickerMeshes.set(weaponGroup, meshes);
  }

  /** Remove all stickers from a weapon group */
  removeStickers(weaponGroup: THREE.Group): void {
    const existing = this.stickerMeshes.get(weaponGroup);
    if (existing) {
      for (const mesh of existing) {
        weaponGroup.remove(mesh);
        mesh.geometry.dispose();
        const material = mesh.material as THREE.Material;
        const tracked = this.holographicByMaterial.get(material);
        if (tracked) {
          const idx = this.holographicShaders.indexOf(tracked);
          if (idx >= 0) this.holographicShaders.splice(idx, 1);
          this.holographicByMaterial.delete(material);
        }
        material.dispose();
      }
    }
    this.stickerMeshes.delete(weaponGroup);
  }

  /** Apply a name tag to a weapon */
  applyNameTag(weaponGroup: THREE.Group, nameTag: NameTag): void {
    this.removeNameTag(weaponGroup);

    const text = nameTag.text.slice(0, MAX_NAME_TAG_LENGTH);
    if (!text) return;

    // Render name tag text onto a canvas texture
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 32;
    const ctx = canvas.getContext('2d')!;

    // Dark background strip
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Name text
    ctx.fillStyle = `#${nameTag.color.toString(16).padStart(6, '0')}`;
    ctx.font = 'italic 18px "Courier New", monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`"${text}"`, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;

    const geometry = new THREE.PlaneGeometry(0.05, 0.006);
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      side: THREE.FrontSide,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(0, 0.048, -0.02); // Above weapon body
    mesh.renderOrder = 200;
    mesh.userData.nameTag = true;

    weaponGroup.add(mesh);
    this.nameTagMeshes.set(weaponGroup, mesh);
  }

  /** Remove name tag from weapon */
  removeNameTag(weaponGroup: THREE.Group): void {
    const existing = this.nameTagMeshes.get(weaponGroup);
    if (existing) {
      weaponGroup.remove(existing);
      existing.geometry.dispose();
      (existing.material as THREE.Material).dispose();
    }
    this.nameTagMeshes.delete(weaponGroup);
  }

  /** Apply holographic iridescence effect to sticker material */
  private applyHolographicEffect(material: THREE.MeshBasicMaterial): void {
    const prev = material.onBeforeCompile;
    material.onBeforeCompile = (shader, renderer) => {
      if (prev) prev.call(material, shader, renderer);

      shader.uniforms.uTime = { value: 0 };
      // Track this shader's uTime so update() can animate the iridescence.
      const uniformsRef = shader.uniforms as unknown as { uTime: { value: number } };
      this.holographicShaders.push(uniformsRef);
      this.holographicByMaterial.set(material, uniformsRef);

      shader.fragmentShader = shader.fragmentShader.replace(
        'void main() {',
        `uniform float uTime;
        void main() {`,
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <dithering_fragment>',
        `// Holographic iridescence
        {
          vec2 holoUV = vUv * 3.0;
          float angle = atan(holoUV.y, holoUV.x);
          float holo = sin(angle * 6.0 + uTime * 2.0) * 0.5 + 0.5;
          vec3 iridescentColor = vec3(
            sin(holo * 6.28) * 0.3 + 0.7,
            sin(holo * 6.28 + 2.09) * 0.3 + 0.7,
            sin(holo * 6.28 + 4.19) * 0.3 + 0.7
          );
          gl_FragColor.rgb = mix(gl_FragColor.rgb, gl_FragColor.rgb * iridescentColor, 0.4);
        }
        #include <dithering_fragment>`,
      );
    };
    material.needsUpdate = true;
  }

  private async loadStickerTexture(def: StickerDefinition): Promise<THREE.Texture> {
    const cached = this.textureCache.get(def.id);
    if (cached) return cached;

    return new Promise((resolve) => {
      this.textureLoader.load(
        def.texturePath,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.minFilter = THREE.LinearMipmapLinearFilter;
          texture.generateMipmaps = true;
          this.textureCache.set(def.id, texture);
          resolve(texture);
        },
        undefined,
        () => {
          const fallback = createFallbackStickerTexture(def);
          this.textureCache.set(def.id, fallback);
          resolve(fallback);
        },
      );
    });
  }

  /** Dispose all cached textures (skips shared placeholder) */
  dispose(): void {
    for (const texture of this.textureCache.values()) {
      if (texture !== sharedPlaceholderTexture) {
        texture.dispose();
      }
    }
    this.textureCache.clear();
    this.holographicShaders.length = 0;
  }
}
